import React, { useState, useEffect, useRef } from "react";
import { Box, Tooltip, Typography, Badge } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import translate from "../../i18n/translate";

const EarthquakeWidget = ({ compact = true }) => {
  const [earthquakes, setEarthquakes] = useState([]);
  const [nearbyQuake, setNearbyQuake] = useState(null);
  const [loading, setLoading] = useState(true);
  const alarmSoundRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const lastAlertedQuake = useRef(null);

  // User location (Palestine/Israel region)
  const userLocation = { lat: 31.9522, lon: 35.2332 };

  // Distance calculation (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const fetchEarthquakes = async () => {
    try {
      // USGS Earthquake API - Last hour, all earthquakes worldwide
      const response = await fetch(
        `https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_hour.geojson`
      );
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const quakesWithDistance = data.features.map((feature) => {
          const [lon, lat, depth] = feature.geometry.coordinates;
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lon,
            lat,
            lon
          );
          return {
            id: feature.id,
            magnitude: feature.properties.mag,
            place: feature.properties.place,
            time: new Date(feature.properties.time),
            lat,
            lon,
            depth,
            distance,
            url: feature.properties.url,
          };
        });

        // Sort by distance
        quakesWithDistance.sort((a, b) => a.distance - b.distance);

        setEarthquakes(quakesWithDistance);

        // Check for nearby earthquakes (within 500km and magnitude > 3.0)
        const nearby = quakesWithDistance.find(
          (q) => q.distance <= 500 && q.magnitude >= 3.0
        );

        if (nearby && nearby.id !== lastAlertedQuake.current) {
          setNearbyQuake(nearby);
          lastAlertedQuake.current = nearby.id;

          // Play alarm for nearby quakes (< 200km)
          if (nearby.distance <= 200) {
            playAlarmSound();
          } else {
            // Soft notification for far quakes (200-500km)
            playNotificationSound();
          }
        } else if (!nearby) {
          setNearbyQuake(null);
        }

        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch earthquakes:", error);
      setLoading(false);
    }
  };

  const playAlarmSound = () => {
    if (alarmSoundRef.current) {
      alarmSoundRef.current.play().catch((e) => console.error("Audio play failed:", e));
    }
  };

  const playNotificationSound = () => {
    if (notificationSoundRef.current) {
      notificationSoundRef.current.play().catch((e) => console.error("Audio play failed:", e));
    }
  };

  useEffect(() => {
    // Fetch immediately
    fetchEarthquakes();

    // Refresh every 2 minutes
    const interval = setInterval(fetchEarthquakes, 120000);

    return () => clearInterval(interval);
  }, []);

  const getAlertColor = () => {
    if (!nearbyQuake) return "#4caf50"; // Green - safe
    if (nearbyQuake.distance <= 50) return "#f44336"; // Red - very close
    if (nearbyQuake.distance <= 200) return "#ff9800"; // Orange - close
    return "#ffeb3b"; // Yellow - far but significant
  };

  const getAlertLevel = () => {
    if (!nearbyQuake) return translate("Safe");
    if (nearbyQuake.distance <= 50) return translate("Very Close");
    if (nearbyQuake.distance <= 200) return translate("Close");
    return translate("Nearby");
  };

  if (!compact) {
    return null; // Only compact version for now
  }

  return (
    <>
      {/* Hidden audio elements */}
      <audio ref={alarmSoundRef} preload="auto">
        {/* Alarm sound for nearby earthquakes - using a beep tone */}
        <source
          src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjWJ0/HHdiwFJHfH8N2QQAoUXrTp66hVFA=="
          type="audio/wav"
        />
      </audio>
      <audio ref={notificationSoundRef} preload="auto">
        {/* Soft notification sound - gentle beep */}
        <source
          src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA="
          type="audio/wav"
        />
      </audio>

      <Tooltip
        title={
          <Box sx={{ p: 1, maxWidth: 300 }}>
            {loading ? (
              <Typography variant="caption">{translate("Loading earthquakes...")}</Typography>
            ) : nearbyQuake ? (
              <>
                <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: "#f44336", mb: 1 }}>
                  ⚠️ {getAlertLevel()} {translate("Earthquake Detected")}!
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>{translate("Magnitude")}:</strong> {nearbyQuake.magnitude.toFixed(1)}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>{translate("Distance")}:</strong> {Math.round(nearbyQuake.distance)} km
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>{translate("Location")}:</strong> {nearbyQuake.place}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>{translate("Depth")}:</strong> {nearbyQuake.depth.toFixed(1)} km
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: "10px", mt: 0.5 }}>
                  {translate("Time")}: {nearbyQuake.time.toLocaleTimeString()}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: "#4caf50" }}>
                  ✅ {translate("No Recent Earthquakes")}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: "10px", mt: 0.5 }}>
                  {translate("Monitoring earthquakes within 500km")}
                </Typography>
                {earthquakes.length > 0 && (
                  <Typography variant="caption" display="block" sx={{ fontSize: "10px", mt: 0.5 }}>
                    {translate("Nearest")}: {earthquakes[0].place} ({Math.round(earthquakes[0].distance)} km)
                  </Typography>
                )}
              </>
            )}
          </Box>
        }
        arrow
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            background: nearbyQuake
              ? `linear-gradient(135deg, ${getAlertColor()} 0%, ${getAlertColor()}dd 100%)`
              : "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: nearbyQuake
              ? `0 0 14px ${getAlertColor()}, 0 3px 8px rgba(0,0,0,0.3)`
              : "0 3px 6px rgba(0,0,0,0.2)",
            animation: nearbyQuake && nearbyQuake.distance <= 200 ? "pulse 1s infinite" : "none",
            "&:hover": {
              transform: "scale(1.1)",
              boxShadow: nearbyQuake
                ? `0 0 18px ${getAlertColor()}, 0 4px 11px rgba(0,0,0,0.4)`
                : "0 4px 8px rgba(0,0,0,0.3)",
            },
            "@keyframes pulse": {
              "0%, 100%": {
                boxShadow: `0 0 14px ${getAlertColor()}, 0 3px 8px rgba(0,0,0,0.3)`,
              },
              "50%": {
                boxShadow: `0 0 28px ${getAlertColor()}, 0 4px 11px rgba(0,0,0,0.4)`,
              },
            },
          }}
        >
          <Badge
            badgeContent={nearbyQuake ? nearbyQuake.magnitude.toFixed(1) : "0"}
            color={nearbyQuake && nearbyQuake.distance <= 200 ? "error" : "primary"}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "7px",
                fontWeight: 700,
                minWidth: "14px",
                height: "14px",
              },
            }}
          >
            <WarningIcon
              sx={{
                fontSize: "20px",
                color: "#fff",
                filter: nearbyQuake ? "drop-shadow(0 0 3px rgba(0,0,0,0.5))" : "none",
              }}
            />
          </Badge>
        </Box>
      </Tooltip>
    </>
  );
};

export default EarthquakeWidget;

